import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Search, Check, Plus, Gift, Sparkles, ChevronDown, ChevronRight, X } from "lucide-react";
import CharacterPortrait from "@/components/ascend/CharacterPortrait";

const TAG_LABELS = {
  all: "all",
  perseverance: "perseverance",
  courage: "courage",
  kindness: "kindness",
  "self-worth": "self-worth",
  study: "study",
  dreams: "dreams",
};

/**
 * QuoteLibraryPanel — browser for the seeded anime quote library.
 * - Loads /api/anime-library
 * - Search across anime title, character, quote text
 * - Tag filter chips
 * - Grouped/collapsible anime sections
 * - Tap-to-add persists to /api/me/anime/library-add (idempotent server-side)
 * - "Random gift" surfaces a random quote as a small modal
 *
 * Props:
 *   currentUserQuotes: Set<string> — the exact text of the user's already-added
 *                                     quotes (case-insensitive). Used to render
 *                                     the "added" state.
 *   onAdded(entry): called when a quote is added, to let parent refresh.
 */
export default function QuoteLibraryPanel({ currentUserQuotes = new Set(), onAdded }) {
  const [library, setLibrary] = useState([]); // [{anime_title, quotes:[{id,character,character_slug,quote_text,theme_tags}]}]
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("all");
  const [characterSlug, setCharacterSlug] = useState(null); // filter: {character_slug} or null
  const [expanded, setExpanded] = useState({});
  const [addingId, setAddingId] = useState(null);
  const [gift, setGift] = useState(null);
  const [addedIds, setAddedIds] = useState(new Set()); // library_quote_ids added this session

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/anime-library");
        setLibrary(data.items || []);
        setTags(data.available_tags || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const norm = (s) => (s || "").toString().toLowerCase();
  const userSet = useMemo(() => {
    const set = new Set();
    (currentUserQuotes || []).forEach((t) => set.add(norm(t).trim()));
    return set;
  }, [currentUserQuotes]);

  const filtered = useMemo(() => {
    const needle = norm(q).trim();
    return library
      .map((anime) => {
        const quotes = (anime.quotes || []).filter((quote) => {
          if (characterSlug && quote.character_slug !== characterSlug) return false;
          if (tag !== "all" && !(quote.theme_tags || []).includes(tag)) return false;
          if (needle) {
            const hay =
              norm(anime.anime_title) + " " + norm(quote.character) + " " + norm(quote.quote_text);
            if (!hay.includes(needle)) return false;
          }
          return true;
        });
        return { ...anime, quotes };
      })
      .filter((a) => a.quotes.length > 0);
  }, [library, q, tag, characterSlug]);

  // Currently-selected character name (for the "clear filter" chip)
  const currentCharacter = useMemo(() => {
    if (!characterSlug) return null;
    for (const anime of library) {
      for (const quote of anime.quotes || []) {
        if (quote.character_slug === characterSlug) {
          return { name: quote.character, portrait_url: quote.character_portrait_url, anime: anime.anime_title };
        }
      }
    }
    return null;
  }, [library, characterSlug]);

  const totalQuotes = useMemo(
    () => library.reduce((n, a) => n + (a.quotes?.length || 0), 0),
    [library]
  );

  const toggleAnime = (title) => setExpanded((e) => ({ ...e, [title]: !e[title] }));

  const isAdded = (quote) => addedIds.has(quote.id) || userSet.has(norm(quote.quote_text).trim());

  const addQuote = async (quote) => {
    if (isAdded(quote)) return;
    setAddingId(quote.id);
    try {
      const { data } = await api.post("/me/anime/library-add", { library_quote_id: quote.id });
      if (data.reason === "quote_limit_reached") {
        toast.info("You've saved 3 quotes for this anime — clear one first if you want a new favourite.");
      } else if (data.already_present) {
        // Silently mark as added (edge case)
        setAddedIds((s) => new Set(s).add(quote.id));
      } else if (data.added) {
        setAddedIds((s) => new Set(s).add(quote.id));
        toast.success("Added to your quotes.");
        onAdded && onAdded(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingId(null);
    }
  };

  const pickGift = () => {
    if (!library.length) return;
    const flat = library.flatMap((a) =>
      (a.quotes || []).map((qq) => ({ ...qq, anime_title: a.anime_title }))
    );
    if (!flat.length) return;
    setGift(flat[Math.floor(Math.random() * flat.length)]);
  };

  return (
    <div data-testid="quote-library-panel">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
        <span className="eyebrow">quote library</span>
        <span className="text-xs text-muted ml-1">{totalQuotes} lines · {library.length} anime</span>
        <button
          onClick={pickGift}
          className="ml-auto btn-ghost text-xs flex items-center gap-1"
          data-testid="quote-library-gift"
          title="Surprise me with a quote"
        >
          <Gift size={12} strokeWidth={1.75} style={{ color: "var(--accent)" }} /> Random gift
        </button>
      </div>

      {/* Search */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-full mb-3"
        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      >
        <Search size={14} strokeWidth={1.5} style={{ color: "var(--text-muted)" }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search anime, character, or a phrase…"
          className="bg-transparent flex-1 text-sm outline-none"
          style={{ color: "var(--text)" }}
          data-testid="quote-library-search"
        />
      </div>

      {/* Tag chips + character filter chip */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="quote-library-tags">
        {currentCharacter && (
          <button
            onClick={() => setCharacterSlug(null)}
            className="text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition-colors"
            style={{
              background: "color-mix(in oklab, var(--accent) 22%, var(--surface))",
              color: "var(--accent)",
              border: "1px solid var(--border)",
            }}
            data-testid="quote-library-clear-character"
            title="Clear character filter"
          >
            <X size={10} strokeWidth={2} />
            {currentCharacter.name}
          </button>
        )}
        {["all", ...(tags || [])].map((t) => {
          const active = tag === t;
          return (
            <button
              key={t}
              onClick={() => setTag(t)}
              className="text-[11px] px-2.5 py-1 rounded-full transition-colors"
              style={{
                background: active ? "var(--primary)" : "var(--surface-2)",
                color: active ? "#fff" : "var(--text)",
                border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
              }}
              data-testid={`quote-library-tag-${t}`}
            >
              {TAG_LABELS[t] || t}
            </button>
          );
        })}
      </div>

      {/* Anime list */}
      {loading ? (
        <div className="text-sm text-muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-2xl p-5 text-sm text-muted italic text-center"
          style={{ background: "var(--surface-2)", border: "1px dashed var(--border)" }}
          data-testid="quote-library-empty"
        >
          {currentCharacter
            ? `No ${currentCharacter.name} quotes match those tags — try clearing tags.`
            : "Nothing matches this filter — try another tag or clear the search."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="quote-library-list">
          {filtered.map((anime) => {
            const isOpen = !!expanded[anime.anime_title] || !!q || !!characterSlug; // auto-open when searching or filtering by character
            return (
              <div
                key={anime.anime_title}
                className="rounded-2xl overflow-hidden"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
              >
                <button
                  onClick={() => toggleAnime(anime.anime_title)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left"
                  data-testid={`quote-library-anime-${anime.anime_title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="font-display text-base flex-1" style={{ color: "var(--text)" }}>
                    {anime.anime_title}
                  </span>
                  <span className="text-[11px] text-muted">{anime.quotes.length} lines</span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-2">
                    {anime.quotes.map((quote) => {
                      const added = isAdded(quote);
                      return (
                        <div
                          key={quote.id}
                          className="rounded-xl p-3 flex items-start gap-3"
                          style={{
                            background: added ? "var(--surface-2)" : "color-mix(in oklab, var(--primary-soft) 22%, var(--surface))",
                            border: "1px solid var(--border)",
                            opacity: added ? 0.65 : 1,
                          }}
                        >
                          <CharacterPortrait
                            url={quote.character_portrait_url}
                            initial={(quote.character || "?").trim().charAt(0).toUpperCase()}
                            size={36}
                            onClick={() => setCharacterSlug((s) => (s === quote.character_slug ? null : quote.character_slug))}
                            active={characterSlug === quote.character_slug}
                            label={`Filter to ${quote.character}`}
                            testid={`quote-library-portrait-${quote.character_slug}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="italic leading-snug" style={{ color: "var(--text)", fontFamily: "serif" }}>
                              "{quote.quote_text}"
                            </div>
                            <div className="text-[11px] text-muted mt-1">
                              — {quote.character || "unknown"}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {(quote.theme_tags || []).map((tt) => (
                                <span
                                  key={tt}
                                  className="text-[10px] px-1.5 py-0.5 rounded-full"
                                  style={{
                                    background: "color-mix(in oklab, var(--accent) 12%, transparent)",
                                    color: "var(--accent)",
                                  }}
                                >
                                  {tt}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={() => addQuote(quote)}
                            disabled={added || addingId === quote.id}
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5 disabled:cursor-default"
                            style={{
                              background: added ? "var(--surface)" : "var(--primary)",
                              color: added ? "var(--text-muted)" : "#fff",
                              border: `1px solid ${added ? "var(--border)" : "var(--primary)"}`,
                            }}
                            aria-label={added ? "Already in your collection" : "Add to your collection"}
                            title={added ? "In your collection" : "Add to your collection"}
                            data-testid={`quote-library-add-${quote.id}`}
                          >
                            {added ? <Check size={14} strokeWidth={2} /> : <Plus size={14} strokeWidth={2} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {gift && (
        <GiftModal
          quote={gift}
          onClose={() => setGift(null)}
          onAdd={() => { addQuote(gift); setGift(null); }}
          added={isAdded(gift)}
          onFilterToCharacter={() => { setCharacterSlug(gift.character_slug); setGift(null); }}
        />
      )}
    </div>
  );
}

function GiftModal({ quote, onClose, onAdd, added, onFilterToCharacter }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "color-mix(in oklab, var(--text) 40%, transparent)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="quote-library-gift-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl p-7 fade-up text-center"
        style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
      >
        <div className="flex justify-center mb-3">
          <CharacterPortrait
            url={quote.character_portrait_url}
            initial={(quote.character || "?").trim().charAt(0).toUpperCase()}
            size={64}
            onClick={onFilterToCharacter}
            label={`Filter to ${quote.character}`}
            testid="quote-library-gift-portrait"
          />
        </div>
        <div className="eyebrow mb-2">a small gift</div>
        <div className="text-xl leading-snug italic" style={{ color: "var(--text)", fontFamily: "serif" }}>
          "{quote.quote_text}"
        </div>
        <div className="text-sm text-muted mt-3">— {quote.character || "unknown"} · {quote.anime_title}</div>
        <div className="flex justify-center gap-2 mt-6">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="quote-library-gift-close">
            Close
          </button>
          {!added && (
            <button onClick={onAdd} className="btn-primary text-sm flex items-center gap-2" data-testid="quote-library-gift-add">
              <Plus size={14} strokeWidth={1.75} /> Keep this
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


